import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  IconName,
  Intent,
  NonIdealState,
  Tooltip,
} from '@blueprintjs/core';

import { useAppDispatch, useAppSelector } from '../../../hooks';

import {
  disableItem,
  downloadVideo,
  getAllWavelights,
  setLoading,
  saveItemsPerPage,
} from '../reducer';
import { WAVELIGHT_WAVES, EDIT_WAVELIGHTS } from '../routes';
import { Wavelight, WavelightsState } from '../types';

import { formatDate, mapErrorMessage } from '../../../utils';
import Loading from '../../../components/Loading';
import Toaster from '../../../components/Toaster';

import { PaddedContainer, WavelightsContainer } from './styles';
import Table from '../../../components/TableItems/TableItems';
import { usePaginationSearchParams } from '../../users/components/pagination/hooks';

const Wavelights = () => {
  const dispatch = useAppDispatch();
  const state: WavelightsState = useAppSelector((state) => state.wavelight);
  const navigate = useNavigate();

  const [isDisableDialogOpen, showDisableDialog] = useState(false);
  const [disablingId, setDisablingId] = useState('');

  const { currentPage, setCurrentPage } = usePaginationSearchParams();
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isLoadingItemsPerPage] = useState(false);
  const itemsPerPageOptions = [10, 20, 30, 40, 50];
  const [localStorageLoaded, setLocalStorageLoaded] = useState(false);

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    dispatch(saveItemsPerPage(newItemsPerPage));
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    dispatch(setLoading(true));
    dispatch(getAllWavelights({ page: 1, limit: newItemsPerPage }))
      .then(() => dispatch(setLoading(false)))
      .catch(() => dispatch(setLoading(false)));
  };

  useEffect(() => {
    const savedItemsPerPage = localStorage.getItem('@wavelight/itemsPerPage');
    if (savedItemsPerPage) {
      setItemsPerPage(parseInt(savedItemsPerPage));
    }
    setLocalStorageLoaded(true);
  }, []);

  useEffect(() => {
    if (localStorageLoaded) {
      dispatch(setLoading(true));
      dispatch(getAllWavelights({ page: currentPage, limit: itemsPerPage }))
        .then(() => dispatch(setLoading(false)))
        .catch(() => dispatch(setLoading(false)));
    }
  }, [dispatch, currentPage, itemsPerPage, localStorageLoaded]);

  const showToasterMessage = useCallback(
    async (message: React.ReactChild, intent: Intent, icon: IconName) => {
      (await Toaster).show({
        message,
        intent,
        icon,
      });
    },
    [],
  );

  // Initial loading of Wavelight Items
  useEffect(() => {
    dispatch(setLoading(true));
    dispatch(getAllWavelights({ page: currentPage, limit: itemsPerPage }));
  }, [dispatch, currentPage, itemsPerPage]);

  // Disable Item
  const openDisablePopup = useCallback(
    (id: string) => {
      showDisableDialog(true);
      setDisablingId(id);
    },
    [showDisableDialog, setDisablingId],
  );

  const closeDisableDialog = useCallback(() => {
    showDisableDialog(false);
    setDisablingId('');
  }, [showDisableDialog, setDisablingId]);

  const doDisableItem = useCallback(
    (id: string) => {
      dispatch(disableItem(id));
    },
    [dispatch],
  );

  // Download item
  const doDownloadVideo = useCallback(
    (id: string) => {
      dispatch(downloadVideo(id));
    },
    [dispatch],
  );

  // Edit Item
  const editItem = useCallback(
    (id: string) => {
      navigate(EDIT_WAVELIGHTS(id));
    },
    [navigate],
  );

  // Deactivate item
  const deactivateItem = useCallback(() => {
    showToasterMessage(
      <>
        Desativando item: <strong>{disablingId}</strong>
      </>,
      Intent.DANGER,
      'warning-sign',
    );
    doDisableItem(disablingId);
    closeDisableDialog();
  }, [disablingId, closeDisableDialog, doDisableItem, showToasterMessage]);

  // Navigate to Calculation Screen
  const openCalculations = useCallback(
    (id: string) => {
      navigate(WAVELIGHT_WAVES(id));
    },
    [navigate],
  );

  if (state.error) {
    return (
      <PaddedContainer>
        <NonIdealState
          icon="issue"
          title={<>{mapErrorMessage(state.error)}</>}
        />
      </PaddedContainer>
    );
  }

  if (state.isLoading) {
    return <Loading message="Loading" />;
  }

  if (state.wavelightsResponse?.wavelights.length === 0) {
    return (
      <PaddedContainer>
        <NonIdealState icon="search" title={'No Items Found'} />
      </PaddedContainer>
    );
  }

  return (
    <WavelightsContainer>
      {isLoadingItemsPerPage && <Loading message="Loading" />}
      <Table<Wavelight>
        rowKey="_id"
        data={state.wavelightsResponse?.wavelights || []}
        pagination={{
          currentPage,
          totalPages: state?.wavelightsResponse?.totalPages || 0,
          onPageChange: (pageNumber) => setCurrentPage(pageNumber),
        }}
        columns={[
          {
            title: 'ID',
            render: (record) => record?._id,
          },
          {
            title: 'User',
            render: (record) =>
              record.user?.username || 'No username available',
          },
          {
            title: 'Classification',
            render: (record) =>
              record.tags?.join(', ') || 'No classification available',
          },
          {
            title: 'Processed',
            render: (record) =>
              record.processedResearch.toString() || 'No available',
          },
          {
            title: 'Mode',
            render: (record) => record.mode,
          },
          {
            title: 'Status',
            render: (record) => record.status,
          },
          {
            title: 'Created',
            render: (record) => formatDate(record.createdAt.toString()),
          },
          {
            title: 'Last Modified',
            render: (record) => formatDate(record.updatedAt.toString()),
          },
          {
            title: 'Action',
            render: (record) => (
              <>
                <Tooltip
                  content={<span>Desativar</span>}
                  openOnTargetFocus={false}
                  usePortal={false}
                >
                  <Button
                    disabled={!record.active}
                    onClick={() => openDisablePopup(record._id)}
                    icon={'trash'}
                    intent={Intent.DANGER}
                  />
                </Tooltip>
                <Tooltip
                  content={<span>Download Video</span>}
                  openOnTargetFocus={false}
                  usePortal={false}
                >
                  <Button
                    disabled={!record.active}
                    onClick={() => doDownloadVideo(record._id)}
                    icon={'cloud-download'}
                  />
                </Tooltip>
                <Tooltip
                  content={<span>Editar</span>}
                  openOnTargetFocus={false}
                  usePortal={false}
                >
                  <Button onClick={() => editItem(record._id)} icon={'edit'} />
                </Tooltip>
                <Tooltip
                  content={<span>Cálculos</span>}
                  openOnTargetFocus={false}
                  usePortal={false}
                >
                  <Button
                    onClick={() => openCalculations(record._id)}
                    icon={'function'}
                  />
                </Tooltip>
              </>
            ),
          },
        ]}
        itemsPerPageOptions={itemsPerPageOptions || []}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
      <>
        <Alert
          isOpen={isDisableDialogOpen}
          onClose={closeDisableDialog}
          canEscapeKeyCancel
          canOutsideClickCancel
          icon="trash"
          intent={Intent.DANGER}
          cancelButtonText="Cancelar"
          confirmButtonText="Desativar"
          onCancel={closeDisableDialog}
          onConfirm={deactivateItem}
        >
          Tem certeza que deseja desativar este item?
        </Alert>
      </>
    </WavelightsContainer>
  );
};

export default Wavelights;
